"use client";

import React from "react";
import {
  Bell,
  Package,
  ShoppingCart,
  AlertTriangle,
  Users,
  Tag,
  CreditCard,
  Truck,
  CheckCircle,
  Circle,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Notification, NotificationType } from "@/types/models/notification";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (notificationId: string, read: boolean) => void;
  onDelete: (notificationId: string) => void;
}

const getNotificationIcon = (type: NotificationType) => {
  const iconClass = "h-5 w-5";

  switch (type) {
    case "order":
      return <ShoppingCart className={cn(iconClass, "text-blue-600")} />;
    case "inventory":
      return <Package className={cn(iconClass, "text-orange-600")} />;
    case "system":
      return <Bell className={cn(iconClass, "text-gray-600")} />;
    case "alert":
      return <AlertTriangle className={cn(iconClass, "text-red-600")} />;
    case "customer":
      return <Users className={cn(iconClass, "text-green-600")} />;
    case "product":
      return <Tag className={cn(iconClass, "text-purple-600")} />;
    case "payment":
      return <CreditCard className={cn(iconClass, "text-teal-600")} />;
    case "shipping":
      return <Truck className={cn(iconClass, "text-indigo-600")} />;
    default:
      return <Bell className={cn(iconClass, "text-gray-600")} />;
  }
};

const getNotificationBgColor = (type: NotificationType) => {
  switch (type) {
    case "order":
      return "bg-blue-100 dark:bg-blue-950/30";
    case "inventory":
      return "bg-orange-100 dark:bg-orange-950/30";
    case "system":
      return "bg-gray-100 dark:bg-gray-900/30";
    case "alert":
      return "bg-red-100 dark:bg-red-950/30";
    case "customer":
      return "bg-green-100 dark:bg-green-950/30";
    case "product":
      return "bg-purple-100 dark:bg-purple-950/30";
    case "payment":
      return "bg-teal-100 dark:bg-teal-950/30";
    case "shipping":
      return "bg-indigo-100 dark:bg-indigo-950/30";
    default:
      return "bg-gray-100 dark:bg-gray-900/30";
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // Mark as read if unread
    if (!notification.read) {
      onMarkAsRead(notification.id, true);
    }

    // Navigate if there's a link
    if (notification.metadata?.link) {
      navigate(notification.metadata.link);
    }
  };

  const handleToggleRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(notification.id, !notification.read);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
  });

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group relative p-4 border-b border-border transition-all duration-200",
        notification.metadata?.link && "cursor-pointer hover:bg-accent/50",
        notification.read ? "opacity-60" : "bg-accent/20"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={cn(
            "flex items-center justify-center h-10 w-10 rounded-full flex-shrink-0",
            getNotificationBgColor(notification.type)
          )}
        >
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  "font-medium text-sm",
                  notification.read
                    ? "text-muted-foreground"
                    : "text-foreground"
                )}
              >
                {notification.title}
              </p>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {notification.message}
              </p>
              <p className="text-xs text-muted-foreground mt-2">{timeAgo}</p>
            </div>

            {/* Unread indicator */}
            {!notification.read && (
              <div className="h-2 w-2 bg-primary rounded-full flex-shrink-0 mt-1" />
            )}
          </div>
        </div>
      </div>

      {/* Action buttons - show on hover */}
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleRead}
          className="h-8 w-8 p-0"
          title={notification.read ? "Mark as unread" : "Mark as read"}
        >
          {notification.read ? (
            <Circle className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          className="h-8 w-8 p-0 hover:text-destructive"
          title="Delete notification"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
